import React, {useEffect, useMemo, useState} from 'react'
import styled from "styled-components";
import {Button, DatePicker, Descriptions, Form, Input, message, Modal, Radio, Space} from "antd";
import dayjs, {Dayjs} from "dayjs";
import {useNavigate} from "react-router-dom";
import {useQuery, useQueryClient} from "react-query";
import {REPO} from "../../Repository/repo";
import useUser from "../../Hooks/useUser";
import {queryKeys} from "../../Constant/QueryKeys";
import PersonSelect from "../../Components/PersonSelect/PersonSelect";
import HtmlEditor from "../../Components/HtmlEditor/HtmlEditor";
import {Uploader} from "../../Components";
import {VACATION_OPTIONS, VACATION_TYPE_OPTIONS} from "../../Constant/Vacation";
import {RequestVacationApprovalBody} from "../../Model/VacationModel";
import {FileModel} from "../../Model/Common";

const Wrap = styled.div`
  .ant-descriptions-item-label {
    width: 140px;
  }
`

const computeDays = (range: [Dayjs, Dayjs] | null, fraction: number): number => {
    if (!range || !range[0] || !range[1]) return 0
    const days = range[1].diff(range[0], 'day') + 1
    return +(days * fraction).toFixed(3)
}

const VacationRequestForm: React.FC<{id?: string | null}> = ({id}) => {
    const {userData} = useUser()
    const navigate = useNavigate()
    const [form] = Form.useForm()
    const [vacationKey, setVacationKey] = useState<string>("연차")
    const [range, setRange] = useState<[Dayjs, Dayjs] | null>(null)
    const [submitting, setSubmitting] = useState(false)
    const queryClient = useQueryClient()

    const currentOption = VACATION_OPTIONS.find(o => o.key === vacationKey) || VACATION_OPTIONS[0]
    const vacationType = currentOption.type
    const unitFraction = currentOption.unit
    const isPartial = unitFraction < 1.0
    const periodLabel = currentOption.period || ""
    const isEdit = !!id

    useEffect(() => {
        if (!id) return
        ;(async () => {
            try {
                const res = await REPO.VacationApproval.Detail(id)
                const vac = res.data.vacation_approval
                const personArr = res.data.persons
                if (vac) {
                    const t = vac.vacation_type
                    const u = Number(vac.vacation_unit_fraction)
                    const p = vac.vacation_period_label || ""
                    const matched = VACATION_OPTIONS.find(o => o.type === t && o.unit === u && (o.period ?? "") === p)
                        || VACATION_OPTIONS.find(o => o.type === t && o.unit === u)
                        || VACATION_OPTIONS.find(o => o.type === t)
                    if (matched) setVacationKey(matched.key)
                    setRange([dayjs(vac.vacation_date_start), dayjs(vac.vacation_date_end)])
                }
                form.setFieldsValue({
                    request_subject: vac.request_subject,
                    request_reason: vac.request_reason,
                    attach_file: vac.request_extra_attach_file || [],
                    person_1st: personArr.find((p: any) => p.person_type === "1차결재자")?.employee_seq || null,
                    person_2nd: personArr.find((p: any) => p.person_type === "2차결재자")?.employee_seq || null,
                    person_3rd: personArr.find((p: any) => p.person_type === "3차결재자")?.employee_seq || null,
                    person_referer: personArr.find((p: any) => p.person_type === "참조자")?.employee_seq || null,
                })
            } catch (e: any) {
                message.error(e?.response?.data?.message || "기존 데이터를 불러오지 못했습니다.")
            }
        })()
    }, [id])


    const computedDays = useMemo(() => computeDays(range, unitFraction), [range, unitFraction])
    const currentTypeOption = VACATION_TYPE_OPTIONS.find(o => o.value === vacationType)
    const affectsAnnual = currentTypeOption?.affectsAnnual ?? 0
    const payRatio = currentTypeOption?.defaultPayRatio ?? 100

    const employeeSeq = userData?.data?.seq
    const baseYear = dayjs().year()
    const {data: annualSummary} = useQuery(
        [...queryKeys.vacationAnnualDetail, employeeSeq, baseYear],
        async () => {
            if (!employeeSeq) return null
            const res = await REPO.Vacation.GetAnnualDetail(employeeSeq, baseYear)
            return res.data
        },
        {enabled: !!employeeSeq}
    )

    const onSubmit = async (values: any) => {
        if (!range) {
            message.error("사용날짜를 선택해 주세요.")
            return
        }
        if (computedDays <= 0) {
            message.error("사용일수가 올바르지 않습니다.")
            return
        }
        if (!values.request_reason || !String(values.request_reason).trim()) {
            message.error("사유를 입력해 주세요.")
            return
        }
        if (!values.person_1st) {
            message.error("1차 결재자를 선택해 주세요.")
            return
        }
        const subjectAuto = `${values.request_subject || ""}`.trim()
        const dateStart = range[0].format("YYYY-MM-DD")
        const dateEnd = range[1].format("YYYY-MM-DD")
        const filesList: FileModel[] = values.attach_file || []

        const body: RequestVacationApprovalBody = {
            vacation_approval: {
                vacation_type: vacationType,
                vacation_date_start: dateStart,
                vacation_date_end: dateEnd,
                vacation_unit_fraction: unitFraction,
                vacation_period_label: isPartial ? periodLabel : "",
                vacation_used_days: computedDays,
                vacation_pay_ratio: payRatio,
                vacation_affects_annual: affectsAnnual as 0 | 1,
                request_subject: subjectAuto || `${vacationType} ${computedDays}일 (${dateStart}${dateStart !== dateEnd ? ` ~ ${dateEnd}` : ""})`,
                request_reason: values.request_reason || "",
                request_extra_attach_file: filesList.map((f: any) => f.file_uri || f.uri || f.url || f),
            },
            person: {
                "1st": values.person_1st,
                "2nd": values.person_2nd,
                "3rd": values.person_3rd,
                referer: values.person_referer,
            }
        }

        const submit = async (): Promise<boolean> => {
            try {
                setSubmitting(true)
                if (isEdit) {
                    await REPO.VacationApproval.Modify(id!, body)
                } else {
                    await REPO.VacationApproval.Create(body)
                }
                queryClient.invalidateQueries(queryKeys.vacationCalendar)
                queryClient.invalidateQueries(queryKeys.vacationAnnual)
                queryClient.invalidateQueries(queryKeys.vacationAnnualDetail)
                queryClient.invalidateQueries(queryKeys.vacationApproval)
                return true
            } catch (e: any) {
                message.error(e?.response?.data?.message || (isEdit ? "수정에 실패했습니다." : "등록에 실패했습니다."))
                return false
            } finally {
                setSubmitting(false)
            }
        }

        const remaining = Number(annualSummary?.summary?.remaining ?? 0)
        const isOver = affectsAnnual && computedDays > remaining
        const isCondolenceWithoutFile = vacationType === "경조휴가" && filesList.length === 0

        const content = isOver
            ? `연차를 초과 사용하였습니다. (잔여 ${remaining}일 / 신청 ${computedDays}일) 계속 신청하시겠습니까?`
            : isCondolenceWithoutFile
                ? "경조 증빙(청첩장/부고장 등) 첨부를 권장합니다. 첨부 없이 계속 신청하시겠습니까?"
                : (isEdit ? "수정하시겠습니까?" : "신청하시겠습니까?")

        Modal.confirm({
            title: "확인",
            content,
            okText: "예",
            cancelText: "아니오",
            onOk: async () => {
                const ok = await submit()
                if (!ok) return
                setTimeout(() => {
                    if (isEdit) {
                        message.success("휴가신청서가 수정되었습니다.")
                        navigate("/Vacation/Approval")
                    } else {
                        message.success("휴가신청서가 등록되었습니다.")
                        navigate("/Vacation/Approval")
                    }
                }, 0)
            },
        })
    }

    const onCancel = () => {
        Modal.confirm({
            title: "확인",
            content: "취소하시겠습니까?",
            okText: "예",
            cancelText: "아니오",
            onOk: () => navigate(-1),
        })
    }

    return (
        <Wrap>
            <Form form={form} onFinish={onSubmit} layout={"horizontal"}>
                <Descriptions bordered column={2}>
                    <Descriptions.Item label={"기안자"} span={1}>{userData?.data?.employee_name || "본인"}</Descriptions.Item>
                    <Descriptions.Item label={"기안일"} span={1}>{dayjs().format("YYYY-MM-DD")}</Descriptions.Item>
                    <Descriptions.Item label={"휴가종류"} span={2}>
                        <Radio.Group
                            value={vacationKey}
                            onChange={(e) => {
                                const next = VACATION_OPTIONS.find(o => o.key === e.target.value)
                                setVacationKey(e.target.value)
                                if (next && next.unit < 1.0 && range && !range[0].isSame(range[1])) {
                                    setRange([range[0], range[0]])
                                }
                            }}
                        >
                            {VACATION_OPTIONS.map(o => (
                                <Radio key={o.key} value={o.key}>{o.label}</Radio>
                            ))}
                        </Radio.Group>
                    </Descriptions.Item>
                    <Descriptions.Item label={"총 지급연차"} span={1}>
                        <b>{annualSummary?.summary.granted ?? 0}</b>일
                    </Descriptions.Item>
                    <Descriptions.Item label={"잔여연차"} span={1}>
                        <b style={{color: '#1677ff'}}>{annualSummary?.summary.remaining ?? 0}</b>일
                    </Descriptions.Item>
                    <Descriptions.Item label={"사용날짜"} span={2}>
                        <Space wrap>
                            {isPartial ? (
                                <DatePicker
                                    value={range?.[0] as any}
                                    onChange={(v) => v ? setRange([v as Dayjs, v as Dayjs]) : setRange(null)}
                                />
                            ) : (
                                <DatePicker.RangePicker
                                    value={range as any}
                                    onChange={(v) => {
                                        if (!v) setRange(null)
                                        else setRange([v[0] as Dayjs, v[1] as Dayjs])
                                    }}
                                />
                            )}
                            <span>연차차감 <b>{affectsAnnual ? computedDays : 0}</b>일</span>
                        </Space>
                        <div style={{fontSize: 12, color: "#999"}}>
                            * 주말이 포함되어야 하는 경우 휴가신청서를 별도로 작성해 주세요.
                        </div>
                    </Descriptions.Item>
                    <Descriptions.Item label={"제목"} span={2}>
                        <Form.Item name={"request_subject"} noStyle>
                            <Input placeholder={"미입력 시 자동 생성"}/>
                        </Form.Item>
                    </Descriptions.Item>
                    <Descriptions.Item label={"사유"} span={2}>
                        <Form.Item name={"request_reason"} noStyle>
                            <HtmlEditor/>
                        </Form.Item>
                    </Descriptions.Item>
                    <Descriptions.Item label={"첨부파일"} span={2}>
                        <Form.Item name={"attach_file"} noStyle>
                            <Uploader multiple/>
                        </Form.Item>
                    </Descriptions.Item>
                </Descriptions>
                <Descriptions layout={"vertical"} column={4} bordered>
                    <Descriptions.Item label={"1차결재자"}>
                        <Form.Item name={"person_1st"}>
                            <PersonSelect/>
                        </Form.Item>
                    </Descriptions.Item>
                    <Descriptions.Item label={"2차결재자"}>
                        <Form.Item name={"person_2nd"}>
                            <PersonSelect/>
                        </Form.Item>
                    </Descriptions.Item>
                    <Descriptions.Item label={"3차결재자"}>
                        <Form.Item name={"person_3rd"}>
                            <PersonSelect/>
                        </Form.Item>
                    </Descriptions.Item>
                    <Descriptions.Item label={"참조"}>
                        <Form.Item name={"person_referer"}>
                            <PersonSelect/>
                        </Form.Item>
                    </Descriptions.Item>
                </Descriptions>
                <div style={{marginTop: 16, textAlign: "right"}}>
                    <Space>
                        <Button onClick={onCancel}>취소</Button>
                        <Button type={"primary"} htmlType={"submit"} loading={submitting}>{isEdit ? "수정" : "등록"}</Button>
                    </Space>
                </div>
            </Form>
        </Wrap>
    )
}

export default VacationRequestForm
